import {Component, ElementRef, OnDestroy, OnInit} from '@angular/core';
import type {EChartsOption} from 'echarts';
import {Router} from "@angular/router";
import {TranslocoService} from "@ngneat/transloco";
import {NetscopeUtils} from "@app/netscope/netscope-utils";
import {NetscopeUiService} from "@app/services/netscope-ui.service";
import {Subscription, take} from "rxjs";
import {
    DuckDbDashboardData,
    FlowDetail,
    FlowRessourceObject,
    TemporalParameter
} from "@app/netscope/model/data-models";
import { DateTime, Interval } from "luxon";
import {DashboardDataResponse, FlowsRepository, InsideOutsideResponse} from "@app/netscope/model/flows-repository";


// Traffic of one day, as the API sends it for the overall traffic chart
interface DailyTraffic {
  time: number;             // start of the day, in seconds
  exchanged_bytes: number;
}

// Slice of a pie chart; `object` is what a click on the slice filters the flows on
interface PieSlice {
  name: string;
  value: number;
  object?: any;
}

// Colors of the charts, read from the theme of the application (see `.highcharts-light` and `.highcharts-dark` in
// styles.css), so that they follow the dark mode
interface ChartColors {
  text: string;
  gridLines: string;
  palette: string[];
  background: string;       // background of the cards holding the charts
}

const DEFAULT_PALETTE = ["rgb(0, 121, 173)", "rgb(255, 185, 46)", "rgb(102, 209, 255)", "rgb(0, 64, 133)",
  "rgb(255, 142, 61)", "rgb(155, 50, 200)", "rgb(79, 97, 105)", "rgb(224, 34, 0)", "rgb(0, 35, 71)", "rgb(66, 129, 14)"];

// Colors of the bars of the overall traffic chart
const DAY_COLOR = 'rgb(84, 167, 240)';
const SELECTED_DAY_COLOR = 'rgb(255, 99, 132)';
const BOUNDARY_DAY_COLOR = 'rgb(255,211,99)';
const SELECTION_BAND_COLOR = 'rgba(255,136,86,0.25)';

// Days shown at once by the overall traffic chart; older days are reached with its slider
const VISIBLE_DAYS = 90;

// Slices of a donut chart smaller than this share of the total have no label (they remain in its legend and tooltip)
const DONUT_LABEL_MIN_SHARE = 0.01;



@Component({
    standalone: false,
  selector: 'app-netscope-dashboard',
  templateUrl: './netscope-dashboard.component.html',
  styleUrls: ['./netscope-dashboard.component.css']
})
export class NetscopeDashboardComponent implements OnInit, OnDestroy {

  isLoading = false;
  failureMode = false;
  currentDayTimestamp = undefined;
  temporalParameter: TemporalParameter;
  showFlowModal = false;

  selectFlowDetailsModalTitle = "";
  lastDataUuid = "";

  isNetscopeLicenceEnabled = true;
  protected readonly netscopeUtils = NetscopeUtils;

  // Data shown by the charts, kept to draw them again when the theme changes
  graphData: DailyTraffic[] = [];
  originPieData: PieSlice[] = [];
  protocolPieData: PieSlice[] = [];
  emitterPieData: PieSlice[] = [];

  overallTrafficChartOptions: EChartsOption = {};
  originPieChartOptions: EChartsOption = {};
  protocolPieChartOptions: EChartsOption = {};
  emitterPieChartOptions: EChartsOption = {};

  private darkModeSubscription: Subscription;

  // ECharts instance of the overall traffic chart, which days are selected on by dragging over its bars
  private overallTrafficChart: any;

  // Days the overall traffic chart shows, as timestamps of their start (in seconds), once its slider or the mouse wheel
  // moved them: the chart is built again on every load, and keeps showing them instead of going back to the latest days
  private visibleDays: {first: number, last: number} = undefined;

  // Shows the toolbox of the overall traffic chart, with the buttons of its selection tool. Hidden by default: days are
  // selected by dragging over the bars anyway (ECharts would otherwise add a toolbox of its own to the chart).
  showOverallTrafficChartTools = false;

  // Details datagrid's config
  flowsRepository: FlowsRepository;
  flowsRepositoryModal: FlowsRepository;

  flowDetails: FlowDetail[] = [];
  // selectFlowDetails: FlowDetail[] = [];

  amountOfDataProcessed = 0;
  amountOfPacketsProcessed = 0;
  amountOfFlowsProcessed = 0;

  constructor(private netscopeUiService: NetscopeUiService, private route: Router, public translocoService: TranslocoService,
              private host: ElementRef) {
  }

  ngOnInit(): void {

    this.netscopeUiService.setViewLabel("dashboard.name");
    this.netscopeUiService.setIconShape("dashboard");

    this.reloadData();

    this.netscopeUiService.selectedResourcesChangeEmitter.subscribe((comboboxSelectionItem) => {
      this.reloadData();
    });

    this.netscopeUiService.selectedDateChangeEmitter.subscribe(([startTime, endTime, timeResolution]) => {
      this.temporalParameter = {
        startTime: startTime,
        endTime: endTime,
        timeResolution: timeResolution,
        timeSelection: "custom"
      };
      this.netscopeUiService.temporalParametersSubject.next(this.temporalParameter);
      this.updateTemporalParameterHighlighting();
    });

    this.netscopeUiService.temporalParametersSubject.subscribe((newTemporalParameters) => {
      this.temporalParameter = newTemporalParameters;
    });

    this.netscopeUiService.duckDbRefinedDataSubject.subscribe((dashboardData) => {
      this.reloadData();
    })

    // The traffic per day is fetched by the main component after the flows have been published: the bar plot is
    // redrawn whenever it arrives, instead of with the flows, which would show the traffic of the previous load
    this.netscopeUiService.dashboardGraphDataSubject.subscribe((dashboardGraphData) => {
      this.updateGraphData(dashboardGraphData as DailyTraffic[]);
    })

    // The theme class of the application changes along with the dark mode: the charts are drawn again once it did
    this.darkModeSubscription = this.netscopeUiService.darkModeStatusSubject.subscribe(() => {
      setTimeout(() => this.renderAllCharts());
    });
  }

  ngOnDestroy = () => {
    this.darkModeSubscription?.unsubscribe();
  }

  filterFlowsForModal = async (event, params) => {
    this.flowsRepositoryModal = this.flowsRepository.clone();

    let init_where_clause = "where true";

    // Filter on origin
    if (params.hasOwnProperty("filterOrigin")) {
      if (params.filterOrigin === "Outside infrastructure") {
        init_where_clause = "where src_asn_num is not NULL or dst_asn_num is not NULL";
      } else {
        init_where_clause = "where src_asn_num is NULL and dst_asn_num is NULL";
      }
    }
    // Filter on emitter
    if (params.hasOwnProperty("filterEmitterObject")) {
      let emitter: FlowRessourceObject = params.filterEmitterObject;
      // @ts-ignore
      let emitterUuid = emitter.src_resource_uuid;

      init_where_clause = `where src_ipaddr = '${emitter.address}' or src_resource_uuid = '${emitterUuid}' `;
    }
    // Filter on protocol
    if (params.hasOwnProperty("filterProtocol")) {
      let protocol = params.filterProtocol;
      this.selectFlowDetailsModalTitle = `Flows emitted with protocol '${protocol}'`;
      init_where_clause = `where protocol = '${protocol}' `;
    }

    this.flowsRepositoryModal.setWhereClause(this.flowsRepository.whereClause);
    await this.flowsRepositoryModal.init(init_where_clause);

    this.showFlowModal = true;
  }

  // Colors of the current theme, read from the variables the theme class of the application defines
  private chartColors = (): ChartColors => {
    let style = getComputedStyle(this.host.nativeElement);
    let read = (name: string, fallback: string) => style.getPropertyValue(name).trim() || fallback;
    let card = this.host.nativeElement.querySelector('.card');
    let cardBackground = card ? getComputedStyle(card).backgroundColor : '';
    let isTransparent = cardBackground === '' || cardBackground === 'transparent' || cardBackground === 'rgba(0, 0, 0, 0)';
    return {
      text: read('--highcharts-neutral-color-80', '#333333'),
      gridLines: read('--highcharts-neutral-color-10', '#e6e6e6'),
      palette: DEFAULT_PALETTE.map((fallback, i) => read(`--highcharts-color-${i}`, fallback)),
      background: isTransparent ? read('--highcharts-background-color', '#ffffff') : cardBackground,
    };
  }

  renderAllCharts = () => {
    this.renderOverallTrafficChart();
    this.originPieChartOptions = this.buildPieChartOptions(this.originPieData);
    this.protocolPieChartOptions = this.buildPieChartOptions(this.protocolPieData);
    this.emitterPieChartOptions = this.buildPieChartOptions(this.emitterPieData);
  }

  // A click on a bar of the overall traffic chart selects its day
  onOverallTrafficChartClick = (event) => {
    let day = this.graphData[event.dataIndex];
    if (event.componentType !== 'series' || event.seriesType !== 'bar' || day === undefined) {
      return;
    }
    this.selectDays(event.dataIndex, event.dataIndex);
  }

  onOverallTrafficChartInit = (chart) => {
    this.overallTrafficChart = chart;
    this.enableDaysSelectionByDragging();
  }

  // Dragging over the bars selects their days: the brush of the chart is taken as the default action of the mouse. It
  // is taken again whenever the chart is built again, as its options are replaced then.
  private enableDaysSelectionByDragging = () => {
    this.overallTrafficChart?.dispatchAction({
      type: 'takeGlobalCursor',
      key: 'brush',
      brushOption: {brushType: 'lineX', brushMode: 'single'}
    });
  }

  // The slider or the mouse wheel moved the days shown: they are kept for the next time the chart is built
  onOverallTrafficChartDataZoom = () => {
    let zoom = this.overallTrafficChart?.getOption()?.dataZoom?.[0];
    let lastIndex = this.graphData.length - 1;
    if (zoom === undefined || lastIndex < 0) {
      return;
    }
    // The window of the slider is given in percents of the days
    let first = Math.round(zoom.start / 100 * lastIndex);
    let last = Math.round(zoom.end / 100 * lastIndex);
    this.visibleDays = {first: this.graphData[first].time, last: this.graphData[last].time};
  }

  // The days a drag went over are selected, from the start of the first one to the end of the last one
  onOverallTrafficChartBrushEnd = (event) => {
    let range = event?.areas?.[0]?.coordRange;
    if (!Array.isArray(range) || this.graphData.length === 0) {
      return;
    }

    // The bar of a day spans half a day on each side of its index: a day is selected as soon as the drag reaches it
    let lastIndex = this.graphData.length - 1;
    let first = Math.min(lastIndex, Math.max(0, Math.round(Math.min(range[0], range[1]))));
    let last = Math.min(lastIndex, Math.max(0, Math.round(Math.max(range[0], range[1]))));

    this.overallTrafficChart?.dispatchAction({type: 'brush', areas: []});
    this.selectDays(first, last);
  }

  onOriginPieChartClick = (event) => {
    this.filterFlowsForModal(event, {
      "filterOrigin": event.name
    });
  }

  onProtocolPieChartClick = (event) => {
    this.filterFlowsForModal(event, {
      "filterProtocol": event.name,
      "filterProtocolObject": event.data?.object,
    });
  }

  onEmitterPieChartClick = (event) => {
    this.filterFlowsForModal(event, {
      "filterEmitter": event.name,
      "filterEmitterObject": event.data?.object,
    });
  }

  // Selects the period from the start of the first day to the end of the last day (indexes in graphData)
  selectDays = (firstIndex: number, lastIndex: number) => {
    this.currentDayTimestamp = this.graphData[firstIndex].time;

    let startAsDateTime = DateTime.fromSeconds(this.currentDayTimestamp);
    let endAsDateTime = DateTime.fromSeconds(this.graphData[lastIndex].time)
      .plus({ days: 1 })
      .minus({ seconds: 1 });

    this.netscopeUiService.setSelectedDate(startAsDateTime.toSeconds(), endAsDateTime.toSeconds(), "daily");
    this.reloadData();
  }

  // The selected period is drawn on the overall traffic chart: the chart is built again with it
  updateTemporalParameterHighlighting = () => {
    this.renderOverallTrafficChart();
  }

  reloadData = () => {
    this.netscopeUiService.setLoadingState(true, "dashboard");
    console.log("dashboardData.reloadData()");

    this.netscopeUiService.duckDbRefinedDataSubject.pipe(
      take(1),
    ).subscribe(async (duckDbData) => {
      console.log("Dashboard got duckdbData");
      // this.flowDetails = flowDetails;
      this.flowsRepository = duckDbData.flowsRepository.clone();
      this.flowsRepository.setWhereClause(this.flowsRepository.whereClause);
      // this.flowsRepository.refresh({
      //   page: {
      //     current: 1,
      //     size: 20
      //   }
      // });
      // this.flowsRepository.repositoryHasBeenUpdated.emit();
      this.flowsRepository.showFirstPage();

      console.log("reload data (1/5)");
      let dashboardData: DashboardDataResponse = await this.flowsRepository.getDashboardData();
      console.log("reload data (2/5)");
      let insideOutsideRatioData: InsideOutsideResponse = await this.flowsRepository.getInsideOutsideRatioData();
      console.log("reload data (3/5)");
      this.reloadUi(duckDbData, insideOutsideRatioData);
      console.log("reload data (4/5)");

      this.amountOfDataProcessed = dashboardData.amountOfDataProcessed;
      this.amountOfPacketsProcessed = dashboardData.amountOfPacketsProcessed;
      this.amountOfFlowsProcessed = dashboardData.amountOfFlowsProcessed;
      this.netscopeUiService.setLoadingState(false, "dashboard");

      console.log("reload data (5/5)");
      this.updateTemporalParameterHighlighting();
    });
  }

  reloadUi = (dashboardData: DuckDbDashboardData, insideOutsideRatioData: InsideOutsideResponse) => {
    if (this.lastDataUuid !== dashboardData.dashboardDataUuid) {
      console.log("reloadUI");
      this.updateInterIntraTrafficPieChart(insideOutsideRatioData);
      this.updateProtocolPieChart(dashboardData);
      this.updateEmitterPieChart(dashboardData);

      this.lastDataUuid = dashboardData.dashboardDataUuid;
    } else {
      console.log("data already displayed")
    }
  }

  updateInterIntraTrafficPieChart = (dashboardData: InsideOutsideResponse) => {
    this.originPieData = [{
      name: 'Inside infrastructure',
      value: dashboardData.inside.amountOfDataProcessed,
    }, {
      name: 'Outside infrastructure',
      value: dashboardData.outside.amountOfDataProcessed,
    }];
    this.originPieChartOptions = this.buildPieChartOptions(this.originPieData);
  }

  updateProtocolPieChart = async (dashboardData: DuckDbDashboardData) => {
    let protocolRatioData = await dashboardData.flowsRepository.computeProtocolsRatio();

    this.protocolPieData = protocolRatioData.map((protocolSummary) => {
      return {
        name: protocolSummary.protocol,
        object: protocolSummary,
        value: protocolSummary.protocol_total_bytes,
      };
    });
    this.protocolPieChartOptions = this.buildPieChartOptions(this.protocolPieData);
  }

  updateEmitterPieChart = async (dashboardData: DuckDbDashboardData) => {
    let topEmitters = await dashboardData.flowsRepository.computeTopEmitters();

    const sortedEmittersSummary = topEmitters
      .sort((p1, p2) => p2.source_total_bytes - p1.source_total_bytes)
      .slice(0, 20);
    this.emitterPieData = sortedEmittersSummary.map((emitterSummary) => {
      let label = emitterSummary.src_ipaddr;
      if (dashboardData.nodeMapping.has(emitterSummary.src_resource_uuid)) {
        let matchingResource = dashboardData.nodeMapping.get(emitterSummary.src_resource_uuid)
        label = matchingResource.name;
      }
      return {
        name: label,
        object: emitterSummary,
        value: emitterSummary.source_total_bytes
      };
    });
    this.emitterPieChartOptions = this.buildPieChartOptions(this.emitterPieData);
  }

  // Donut charts, after the 'pie-borderRadius' example of ECharts: rounded slices set apart by a border of the color of
  // the card. The slices of at least DONUT_LABEL_MIN_SHARE of the total are labelled beside the donut.
  buildPieChartOptions = (slices: PieSlice[]): EChartsOption => {
    let colors = this.chartColors();

    let total = slices.reduce((sum, slice) => sum + (slice.value || 0), 0);
    let data = slices.map((slice) => {
      let hasLabel = total > 0 && slice.value / total >= DONUT_LABEL_MIN_SHARE;
      return {
        ...slice,
        label: {show: hasLabel},
        labelLine: {show: hasLabel}
      };
    });

    return {
      color: colors.palette,
      tooltip: {
        trigger: 'item',
        formatter: (params: any) =>
          `${params.name}: <b>${NetscopeUtils.formatBytes(params.value)}</b> (${params.percent} %)`
      },
      // The protocols and emitters can be many: the legend scrolls
      legend: {
        type: 'scroll',
        bottom: 0,
        left: 'center',
        textStyle: {color: colors.text},
        pageTextStyle: {color: colors.text}
      },
      series: [{
        type: 'pie',
        radius: ['30%', '55%'],
        center: ['50%', '45%'],
        cursor: 'pointer',
        selectedMode: false,
        itemStyle: {
          borderRadius: 10,
          borderColor: colors.background,
          borderWidth: 2
        },
        percentPrecision: 1,
        // Labels at the end of their connector lines, fitted to the space beside the donut: a long name is wrapped on
        // several lines instead of being cut
        label: {
          position: 'outside',
          color: colors.text,
          formatter: '{b}: {d} %',
          alignTo: 'labelLine',
          bleedMargin: 5,
          overflow: 'break',
          lineHeight: 15
        },
        labelLine: {
          length: 10,
          length2: 10
        },
        data: data
      }]
    };
  }

  updateGraphData = (graphData: DailyTraffic[]) => {
    if (graphData == undefined) {
      // The traffic per day could not be fetched: the bar plot keeps what it shows
      console.warn("No traffic per day, I will not update the bar plot");
      return;
    }

    this.graphData = graphData;
    this.renderOverallTrafficChart();
  }

  renderOverallTrafficChart = () => {
    if (this.temporalParameter == undefined) {
      console.warn("TemporalParameter is not yet set, I will skip the update of graphs for now");
      return;
    }

    let colors = this.chartColors();
    let days = this.graphData;

    let curatedStartTime = this.temporalParameter.startTime === undefined ? Date.now() * 1000 : this.temporalParameter.startTime;
    let curatedEndTime = this.temporalParameter.endTime === undefined ? Date.now() - 24 * 3600 * 1000 : this.temporalParameter.endTime;

    let startAsDt = DateTime.fromSeconds(curatedStartTime);
    let endAsDt = DateTime.fromSeconds(curatedEndTime);
    let interval = Interval.fromDateTimes(startAsDt, endAsDt);

    // The days of the selected period are highlighted, its first and last days with their own color
    let selectedIndexes: number[] = [];
    let data = days.map((d, index) => {
      let timeAsDt = DateTime.fromSeconds(d.time);

      let color = undefined;
      if (timeAsDt.hasSame(startAsDt, "day") || timeAsDt.hasSame(endAsDt, "day")) {
        color = BOUNDARY_DAY_COLOR;
      }
      if (interval.contains(timeAsDt)) {
        color = SELECTED_DAY_COLOR;
      }
      if (color !== undefined) {
        selectedIndexes.push(index);
      }

      return {
        value: d.exchanged_bytes,
        itemStyle: color === undefined ? undefined : {color: color}
      };
    });

    let categories = days.map((d) => String(d.time));
    let dayLabel = (seconds: number) => new Date(seconds * 1000).toLocaleDateString("fr");

    // Position of a time within the bar of its day, from 0 (the start of the day) to 1 (its end); a day is not always
    // 24 hours long (changes of daylight saving time)
    let positionInDay = (dayIndex: number, seconds: number) => {
      let dayStart = days[dayIndex].time;
      let dayEnd = DateTime.fromSeconds(dayStart).plus({days: 1}).toSeconds();
      return Math.min(1, Math.max(0, (seconds - dayStart) / (dayEnd - dayStart)));
    };

    // Band over the selected period, with a dashed line on its start. Its start and end are placed within the bars of
    // their days at their time of the day: a sliding period such as the last 24 hours starts within the bar of
    // yesterday. It is drawn by a custom series from the position of the bars, so that it follows them when the chart
    // is moved or zoomed (a markArea would join the middles of the bars).
    let selectionBand = [];
    if (selectedIndexes.length > 0) {
      let first = selectedIndexes[0];
      let last = selectedIndexes[selectedIndexes.length - 1];
      let startPosition = positionInDay(first, curatedStartTime);
      // A sliding period ('last 24 hours', 'last week'...) ends with the last flows received: ending the band there,
      // within the bar of today, would suggest that the rest of today was left out, while it is only yet to come
      let isSlidingPeriod = this.temporalParameter.timeSelection?.startsWith("last ") ?? false;
      let endsToday = DateTime.fromSeconds(curatedEndTime).hasSame(DateTime.now(), "day");
      let endPosition = isSlidingPeriod && endsToday ? 1 : positionInDay(last, curatedEndTime);
      let bandLabel = `${dayLabel(curatedStartTime)} to ${dayLabel(curatedEndTime)}`;
      selectionBand = [{
        type: 'custom',
        silent: true,
        z: 1,
        tooltip: {show: false},
        // The first two values are days (indexes on the x axis), the last two the positions of the start and end within
        // their bars: none is a volume, so the band does not stretch the y axis
        encode: {x: [0, 1]},
        data: [[first, last, startPosition, endPosition]],
        renderItem: (params: any, api: any) => {
          let area = params.coordSys;
          let bandWidth = api.size([1, 0])[0];
          let leftEdgeOfDay = (dayIndex: number) => api.coord([dayIndex, 0])[0] - bandWidth / 2;
          let left = leftEdgeOfDay(api.value(0)) + api.value(2) * bandWidth;
          let right = leftEdgeOfDay(api.value(1)) + api.value(3) * bandWidth;

          // Only the part of the band inside the visible days is drawn
          let visibleLeft = Math.max(left, area.x);
          let visibleRight = Math.min(right, area.x + area.width);
          if (visibleRight <= visibleLeft) {
            return undefined;
          }

          let children: any[] = [{
            type: 'rect',
            shape: {x: visibleLeft, y: area.y, width: visibleRight - visibleLeft, height: area.height},
            style: {fill: SELECTION_BAND_COLOR}
          }, {
            type: 'text',
            style: {
              text: bandLabel, x: (visibleLeft + visibleRight) / 2, y: area.y + 4,
              align: 'center', verticalAlign: 'top', fill: colors.text
            }
          }];
          if (left >= area.x) {
            children.push({
              type: 'line',
              shape: {x1: left, y1: area.y, x2: left, y2: area.y + area.height},
              style: {stroke: colors.palette[4], lineWidth: 2, lineDash: [6, 4]}
            });
          }
          return {type: 'group', children: children};
        }
      }];
    }

    // More than VISIBLE_DAYS days: some are shown, the others are reached with the slider. The slider only moves the
    // view (filterMode 'none'): filtering the days out would move the band away from the bars.
    let dataZoom = undefined;
    if (days.length > VISIBLE_DAYS) {
      let [firstVisible, lastVisible] = this.visibleDaysWindow(selectedIndexes);
      let window = {startValue: firstVisible, endValue: lastVisible, filterMode: 'none' as const};
      dataZoom = [
        {
          type: 'slider', ...window, height: 20, bottom: 10,
          // The ends of the slider show their days as dates (the categories of the axis are timestamps)
          labelFormatter: (index: number, category: string) => dayLabel(Number(category))
        },
        // Dragging inside the chart selects days (see enableDaysSelectionByDragging): it does not move the view,
        // which the slider and the mouse wheel still do
        {type: 'inside', ...window, moveOnMouseMove: false}
      ];
    }

    this.overallTrafficChartOptions = {
      // Room below the bars for the slider, and for the labels of the days its handles show
      grid: {left: 16, right: 16, top: 30, bottom: dataZoom ? 64 : 16, containLabel: true},
      tooltip: {
        trigger: 'axis',
        axisPointer: {type: 'shadow'},
        formatter: (params: any) => {
          let points = Array.isArray(params) ? params : [params];
          let point = points.find((p) => p.seriesType === 'bar');
          let day = point === undefined ? undefined : days[point.dataIndex];
          if (day === undefined) {
            return '';
          }
          return `${dayLabel(day.time)}<br>Volume: <b>${NetscopeUtils.formatBytes(day.exchanged_bytes)}</b>`;
        }
      },
      xAxis: {
        type: 'category',
        data: categories,
        axisLabel: {
          color: colors.text,
          formatter: (value: string) => DateTime.fromSeconds(Number(value)).toLocaleString({day: '2-digit', month: 'short'})
        }
      },
      yAxis: {
        type: 'value',
        name: 'Volume',
        min: 0,
        nameTextStyle: {color: colors.text},
        axisLabel: {
          color: colors.text,
          formatter: (value: number) => NetscopeUtils.formatBytes(value)
        },
        splitLine: {lineStyle: {color: colors.gridLines}}
      },
      dataZoom: dataZoom,
      // Selection of days by dragging over the bars (see onOverallTrafficChartBrushEnd)
      brush: {
        xAxisIndex: 0,
        brushType: 'lineX',
        brushMode: 'single',
        transformable: false,
        removeOnClick: true,
        // The only buttons of the toolbox, when it is shown: selecting days, and clearing the selection
        toolbox: ['lineX', 'clear'],
        brushStyle: {color: 'rgba(84, 167, 240, 0.2)', borderColor: DAY_COLOR, borderWidth: 1}
      },
      toolbox: {
        show: this.showOverallTrafficChartTools,
        right: 16
      },
      series: [{
        type: 'bar',
        name: 'Volume',
        data: data,
        cursor: 'pointer',
        barCategoryGap: '8%',
        itemStyle: {color: DAY_COLOR},
        z: 2
      }, ...selectionBand]
    };

    // The brush is lost along with the previous options, once the chart has taken the new ones
    setTimeout(() => this.enableDaysSelectionByDragging());
  }

  /**
   * Indexes of the first and last days the overall traffic chart shows: the days it showed before it was built again,
   * or the latest VISIBLE_DAYS days the first time. The window is moved, keeping its width, when the selected days
   * (their indexes) are not all within it, so that the selection is always in view.
   */
  private visibleDaysWindow = (selectedIndexes: number[]): [number, number] => {
    let days = this.graphData;
    let lastIndex = days.length - 1;

    let first = Math.max(0, lastIndex - VISIBLE_DAYS + 1);
    let last = lastIndex;
    if (this.visibleDays !== undefined) {
      let keptFirst = days.findIndex((d) => d.time >= this.visibleDays.first);
      let keptLast = days.map((d) => d.time <= this.visibleDays.last).lastIndexOf(true);
      if (keptFirst >= 0 && keptLast >= keptFirst) {
        first = keptFirst;
        last = keptLast;
      }
    }

    if (selectedIndexes.length > 0) {
      let width = last - first;
      let firstSelected = selectedIndexes[0];
      let lastSelected = selectedIndexes[selectedIndexes.length - 1];
      if (lastSelected > last) {
        last = lastSelected;
        first = Math.max(0, last - width);
      }
      if (firstSelected < first) {
        first = firstSelected;
        last = Math.min(lastIndex, first + width);
      }
    }

    this.visibleDays = {first: days[first].time, last: days[last].time};
    return [first, last];
  }

  switchFocus = (vm) => {
    this.netscopeUiService.switchFocus(vm);
  }
}
